import { useEffect, useState } from 'react'
import { useForm } from 'react-hook-form'
import { toast as message } from 'sonner'
import { Button } from '../../components/ui/button'
import { Card } from '../../components/ui/card'
import { Input } from '../../components/ui/input'
import { Modal } from '../../components/ui/modal'
import { Field, Textarea } from '../../components/ui/controls'
import { QRCode } from '../../components/ui/qr-code'
import { useMerchantStore } from '../../stores/merchantStore'
import {
  getMerchant,
  updateMerchant as saveMerchantSettings,
  uploadMerchantLogo,
} from '../../services/merchant'
import {
  changePassword,
  createTwoFactorSetup,
  enableTwoFactor,
  sendTwoFactorRebindCode,
} from '../../services/auth'
import EmailCodeVerification from '../../components/common/EmailCodeVerification'
import { useEmailCodeVerification } from '../../hooks/useEmailCodeVerification'
import { useAuthStore } from '../../stores/authStore'

const MAX_LOGO_SIZE_BYTES = 2 * 1024 * 1024
const LOGO_MIME_TYPES = ['image/jpeg', 'image/jpg', 'image/png', 'image/webp']
const LOGO_EXTENSIONS = ['.jpg', '.jpeg', '.png', '.webp']

export default function Settings() {
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [logoUploading, setLogoUploading] = useState(false)
  const [passwordSaving, setPasswordSaving] = useState(false)
  const [twoFactorOpen, setTwoFactorOpen] = useState(false)
  const [twoFactorLoading, setTwoFactorLoading] = useState(false)
  const [rebindCodeSending, setRebindCodeSending] = useState(false)
  const [setupSecret, setSetupSecret] = useState<string | null>(null)
  const [setupOtpauthUrl, setSetupOtpauthUrl] = useState<string | null>(null)
  const [setupToken, setSetupToken] = useState<string | null>(null)
  const {
    merchant,
    setMerchant,
    updateMerchant: updateMerchantState,
  } = useMerchantStore()
  const emailVerification = useEmailCodeVerification(
    `rebind:${merchant?.id ?? ''}:${twoFactorOpen}`,
  )
  const updateTwoFactorEnabled = useAuthStore(
    (state) => state.updateTwoFactorEnabled,
  )
  const form = useForm<{
    name?: string
    logo?: string
    website?: string
    description?: string
    phone?: string
  }>()
  const logoPreview = form.watch('logo') as string | undefined
  const passwordForm = useForm<{
    currentPassword: string
    newPassword: string
    confirmPassword: string
    totpCode?: string
  }>()
  const twoFactorForm = useForm<{ emailCode?: string; totpCode?: string }>()
  const websiteLocked =
    merchant?.kycStatus === 'APPROVED' || merchant?.kybStatus === 'APPROVED'

  useEffect(() => {
    getMerchant()
      .then((res) => {
        setMerchant(res.data)
        form.reset({
          name: res.data.name,
          logo: res.data.logo || '',
          website: res.data.website,
          description: res.data.description,
          phone: res.data.phone,
        })
      })
      .finally(() => setLoading(false))
  }, [form, setMerchant])

  const handleSubmit = async (values: {
    name?: string
    logo?: string
    website?: string
    description?: string
    phone?: string
  }) => {
    setSaving(true)
    try {
      const payload = { ...values }
      if (websiteLocked) delete payload.website
      const res = await saveMerchantSettings(payload)
      setMerchant(res.data)
      message.success('設定已保存')
    } catch (error: unknown) {
      message.error(error instanceof Error ? error.message : '保存失敗')
    } finally {
      setSaving(false)
    }
  }

  const handleLogoUpload = async (file: File) => {
    const filename = file.name.toLowerCase()
    const hasAllowedExtension = LOGO_EXTENSIONS.some((extension) =>
      filename.endsWith(extension),
    )
    const hasAllowedType =
      !file.type || LOGO_MIME_TYPES.includes(file.type.toLowerCase())
    if (!hasAllowedExtension || !hasAllowedType) {
      message.error('Logo 僅支援 JPG、PNG 或 WebP')
      return false
    }
    if (file.size > MAX_LOGO_SIZE_BYTES) {
      message.error('Logo 檔案不可超過 2MB')
      return false
    }

    setLogoUploading(true)
    try {
      const res = await uploadMerchantLogo(file)
      form.setValue('logo', res.data.url)
      message.success('Logo 上傳成功')
    } catch (error: unknown) {
      message.error(error instanceof Error ? error.message : 'Logo 上傳失敗')
    } finally {
      setLogoUploading(false)
    }
    return false
  }

  const merchantInitial = merchant?.name?.trim().charAt(0).toUpperCase() || 'M'

  const handlePasswordChange = async (values: {
    currentPassword: string
    newPassword: string
    confirmPassword: string
    totpCode?: string
  }) => {
    if (values.newPassword !== values.confirmPassword) {
      passwordForm.setError('confirmPassword', {
        message: '兩次輸入的密碼不一致',
      })
      return
    }
    setPasswordSaving(true)
    try {
      await changePassword({
        currentPassword: values.currentPassword,
        newPassword: values.newPassword,
        totpCode: values.totpCode,
      })
      passwordForm.reset()
      message.success('密碼已更新')
    } catch (error: unknown) {
      message.error(error instanceof Error ? error.message : '修改密碼失敗')
    } finally {
      setPasswordSaving(false)
    }
  }

  const openTwoFactorModal = () => {
    emailVerification.reset()
    twoFactorForm.reset()
    setSetupSecret(null)
    setSetupOtpauthUrl(null)
    setSetupToken(null)
    setTwoFactorOpen(true)
  }

  const handleSendRebindCode = async () => {
    setRebindCodeSending(true)
    try {
      await emailVerification.send((token) => sendTwoFactorRebindCode(token))
      message.success('郵箱驗證碼已發送')
    } catch (error: unknown) {
      message.error(error instanceof Error ? error.message : '發送驗證碼失敗')
    } finally {
      setRebindCodeSending(false)
    }
  }

  const handleCreateTwoFactorSetup = async () => {
    if (merchant?.twoFactorEnabled && !emailVerification.ready) {
      message.warning('請先完成人機驗證')
      return
    }
    setTwoFactorLoading(true)
    try {
      if (
        merchant?.twoFactorEnabled &&
        !(await twoFactorForm.trigger('emailCode'))
      )
        return
      const res = await createTwoFactorSetup(
        twoFactorForm.getValues('emailCode'),
      )
      setSetupSecret(res.data.secret)
      setSetupOtpauthUrl(res.data.otpauthUrl)
      setSetupToken(res.data.setupToken ?? null)
      message.success(
        merchant?.twoFactorEnabled ? '新的 2FA 設定已生成' : '2FA 設定已生成',
      )
    } catch (error: unknown) {
      message.error(error instanceof Error ? error.message : '生成 2FA 失敗')
    } finally {
      setTwoFactorLoading(false)
    }
  }

  const handleEnableTwoFactor = async () => {
    if (!setupSecret || !(await twoFactorForm.trigger('totpCode'))) return
    setTwoFactorLoading(true)
    try {
      await enableTwoFactor({
        secret: setupSecret,
        totpCode: twoFactorForm.getValues('totpCode')!,
        setupToken: setupToken ?? undefined,
      })
      updateTwoFactorEnabled(true)
      updateMerchantState({ twoFactorEnabled: true })
      setTwoFactorOpen(false)
      setSetupSecret(null)
      setSetupOtpauthUrl(null)
      setSetupToken(null)
      twoFactorForm.reset()
      message.success(
        merchant?.twoFactorEnabled ? '2FA 已重新設定' : '2FA 已啟用',
      )
    } catch (error: unknown) {
      message.error(error instanceof Error ? error.message : '啟用 2FA 失敗')
    } finally {
      setTwoFactorLoading(false)
    }
  }

  const copyText = async (text: string) => {
    await navigator.clipboard.writeText(text)
    message.success('已複製')
  }

  return (
    <div className="space-y-6">
      <h1 className="text-2xl font-semibold">賬戶設定</h1>
      <Card className="max-w-2xl p-5">
        <h2 className="font-semibold">商戶資訊</h2>
        {loading ? (
          <p role="status">載入中…</p>
        ) : (
          <form
            className="space-y-4"
            onSubmit={form.handleSubmit(handleSubmit)}
          >
            <input type="hidden" {...form.register('logo')} />
            <Field label="商戶 Logo">
              <p className="mb-3 text-xs text-muted-foreground">
                JPG、PNG 或 WebP，最大 2MB，建議 1:1、512 × 512px
              </p>
              <div className="flex flex-wrap items-center gap-4">
                {logoPreview ? (
                  <img
                    src={logoPreview}
                    alt="商戶 Logo"
                    className="size-20 rounded-xl border object-cover"
                  />
                ) : (
                  <div className="flex size-20 items-center justify-center rounded-xl bg-primary/10 text-2xl font-semibold text-primary">
                    {merchantInitial}
                  </div>
                )}
                <label className="cursor-pointer rounded-lg border px-3 py-2 text-sm">
                  {logoUploading
                    ? '上傳中…'
                    : logoPreview
                      ? '替換 Logo'
                      : '上傳 Logo'}
                  <input
                    aria-label="上傳 Logo"
                    className="sr-only"
                    type="file"
                    accept={LOGO_EXTENSIONS.join(',')}
                    disabled={logoUploading}
                    onChange={(e) => {
                      const file = e.target.files?.[0]
                      if (file) void handleLogoUpload(file)
                      e.target.value = ''
                    }}
                  />
                </label>
                {logoPreview && (
                  <Button
                    variant="destructive"
                    disabled={logoUploading}
                    onClick={() => form.setValue('logo', '')}
                  >
                    刪除
                  </Button>
                )}
              </div>
            </Field>
            <Field label="郵箱">
              <Input value={merchant?.email} disabled />
            </Field>
            <Field label="商戶名稱">
              <Input value={merchant?.name || ''} disabled />
            </Field>
            <Field label="網站" htmlFor="settings-website">
              <Input
                id="settings-website"
                placeholder="https://your-website.com"
                disabled={websiteLocked}
                {...form.register('website')}
              />
              {websiteLocked && (
                <p className="text-xs text-muted-foreground">
                  KYC 或 KYB 已通過，如需修改網站請聯絡管理員。
                </p>
              )}
            </Field>
            <Field label="商戶描述" htmlFor="settings-description">
              <Textarea
                id="settings-description"
                rows={4}
                placeholder="簡單介紹您的業務"
                {...form.register('description')}
              />
            </Field>
            <Field
              label="電話號碼"
              htmlFor="settings-phone"
              error={form.formState.errors.phone?.message}
            >
              <Input
                id="settings-phone"
                type="tel"
                maxLength={40}
                placeholder="+852 1234 5678"
                {...form.register('phone', {
                  pattern: {
                    value: /^\s*$|^\s*\+?[0-9][0-9 ()-]{4,30}[0-9]\s*$/,
                    message: '請輸入有效的電話號碼',
                  },
                })}
              />
            </Field>
            <Button type="submit" disabled={saving || logoUploading}>
              儲存
            </Button>
          </form>
        )}
      </Card>
      <Card className="max-w-2xl p-5">
        <h2 className="font-semibold">雙重驗證</h2>
        <p>
          {merchant?.twoFactorEnabled
            ? '目前已開啟 TOTP 雙重驗證。'
            : '開啟 TOTP 雙重驗證後，登入與敏感操作都需要 6 位驗證碼。'}
        </p>
        <Button
          className="self-start"
          variant="outline"
          onClick={openTwoFactorModal}
        >
          {merchant?.twoFactorEnabled ? '修改 2FA' : '建立 2FA'}
        </Button>
      </Card>
      <Card className="max-w-2xl p-5">
        <h2 className="font-semibold">修改登入密碼</h2>
        <form
          className="space-y-4"
          onSubmit={passwordForm.handleSubmit(handlePasswordChange)}
        >
          {(
            [
              ['currentPassword', '目前密碼'],
              ['newPassword', '新密碼'],
              ['confirmPassword', '確認新密碼'],
            ] as const
          ).map(([name, label]) => (
            <Field
              key={name}
              label={label}
              htmlFor={`password-${name}`}
              required
              error={passwordForm.formState.errors[name]?.message}
            >
              <Input
                id={`password-${name}`}
                type="password"
                autoComplete={
                  name === 'currentPassword'
                    ? 'current-password'
                    : 'new-password'
                }
                {...passwordForm.register(name, {
                  required: `請輸入${label}`,
                  minLength:
                    name === 'newPassword'
                      ? { value: 8, message: '密碼至少 8 位' }
                      : undefined,
                })}
              />
            </Field>
          ))}
          {merchant?.twoFactorEnabled && (
            <Field
              label="2FA 驗證碼"
              htmlFor="password-totp"
              error={passwordForm.formState.errors.totpCode?.message}
            >
              <Input
                id="password-totp"
                maxLength={6}
                {...passwordForm.register('totpCode', {
                  required: '請輸入 2FA 驗證碼',
                })}
              />
            </Field>
          )}
          <Button type="submit" disabled={passwordSaving}>
            更新密碼
          </Button>
        </form>
      </Card>
      <Modal
        title={merchant?.twoFactorEnabled ? '修改 2FA' : '建立 2FA'}
        open={twoFactorOpen}
        onOpenChange={setTwoFactorOpen}
      >
        <div className="space-y-4">
          {twoFactorOpen && merchant?.twoFactorEnabled && !setupSecret && (
            <>
              <p>
                修改 2FA 前，需先通過寄送到 {merchant?.email} 的郵箱驗證碼。
              </p>
              <Button
                variant="outline"
                onClick={handleSendRebindCode}
                disabled={rebindCodeSending || !emailVerification.canSend}
              >
                發送郵箱驗證碼
              </Button>
              <Field
                label="郵箱驗證碼"
                htmlFor="twofactor-email"
                required
                error={twoFactorForm.formState.errors.emailCode?.message}
              >
                <Input
                  id="twofactor-email"
                  maxLength={6}
                  {...twoFactorForm.register('emailCode', {
                    required: '請輸入郵箱驗證碼',
                  })}
                />
              </Field>
              <EmailCodeVerification verification={emailVerification} />
            </>
          )}
          {!setupSecret ? (
            <Button
              onClick={handleCreateTwoFactorSetup}
              disabled={
                twoFactorLoading ||
                Boolean(merchant?.twoFactorEnabled && !emailVerification.ready)
              }
            >
              生成新的 2FA 設定
            </Button>
          ) : (
            <>
              <p>
                請使用驗證器 App 掃描下方 QR Code；若無法掃描，也可手動輸入
                Secret。
              </p>
              {setupOtpauthUrl && (
                <div className="flex justify-center py-2">
                  <QRCode value={setupOtpauthUrl} size={208} />
                </div>
              )}
              <div className="flex gap-2">
                <Input aria-label="2FA Secret" readOnly value={setupSecret} />
                <Button variant="outline" onClick={() => copyText(setupSecret)}>
                  複製 Secret
                </Button>
              </div>
              {setupOtpauthUrl && (
                <div className="flex gap-2">
                  <Input
                    aria-label="2FA URL"
                    readOnly
                    value={setupOtpauthUrl}
                  />
                  <Button
                    variant="outline"
                    onClick={() => copyText(setupOtpauthUrl)}
                  >
                    複製 URL
                  </Button>
                </div>
              )}
              <Field
                label="新的 2FA 驗證碼"
                htmlFor="twofactor-totp"
                required
                error={twoFactorForm.formState.errors.totpCode?.message}
              >
                <Input
                  id="twofactor-totp"
                  maxLength={6}
                  {...twoFactorForm.register('totpCode', {
                    required: '請輸入新的 2FA 驗證碼',
                  })}
                />
              </Field>
              <Button
                onClick={handleEnableTwoFactor}
                disabled={twoFactorLoading}
              >
                完成 {merchant?.twoFactorEnabled ? '修改' : '啟用'}
              </Button>
            </>
          )}
        </div>
      </Modal>
    </div>
  )
}
